import { safeSessionStorage } from '@toss/storage';

import type { QuizPlanPaymentResponse } from '@/api/gen/_model/quizPlanPaymentResponse';
import { type PaymentResultData } from '@/shared/types/payment';

import { calculateMonthsDuration } from './calculateMonthsDuration';

const STORAGE_KEY_PREFIX = 'paymentData';

class PaymentResultManager {
  private generateStorageKey(orderId: string): string {
    return `${STORAGE_KEY_PREFIX}_${orderId}`;
  }

  save(data: PaymentResultData): string {
    const storageKey = this.generateStorageKey(data.orderId);
    safeSessionStorage.set(storageKey, JSON.stringify(data));
    return storageKey;
  }

  transformData(data: QuizPlanPaymentResponse): PaymentResultData {
    return {
      ...data,
      durationInMonths: calculateMonthsDuration(data.startDate, data.endDate),
    };
  }

  get(storageKey: string): PaymentResultData | null {
    const storedData = safeSessionStorage.get(storageKey);

    if (!storedData) {
      return null;
    }

    try {
      return JSON.parse(storedData) as PaymentResultData;
    } catch (error) {
      console.error('Failed to parse payment complete data:', error);
      return null;
    }
  }

  remove(storageKey: string): void {
    safeSessionStorage.remove(storageKey);
  }

  isValidKey(key: string): boolean {
    return key?.startsWith(STORAGE_KEY_PREFIX) || false;
  }

  getKeyFromUrl(queryKey: string | string[] | undefined): string | null {
    if (!queryKey) return null;

    const key = Array.isArray(queryKey) ? queryKey[0] : queryKey;

    if (!this.isValidKey(key)) {
      return null;
    }

    return key;
  }
}

export const paymentResultManager = new PaymentResultManager();
